import { t, Trans } from "@lingui/macro";
import { createId } from "@paralleldrive/cuid2";
import { DotsSixVertical, Envelope, X } from "@phosphor-icons/react";
import type { CustomField as ICustomField } from "@stylingcv/schema";
import { Button, Input, Popover, PopoverContent, PopoverTrigger, Tooltip } from "@stylingcv/ui";
import { cn } from "@stylingcv/utils";
import { AnimatePresence, Reorder, useDragControls } from "framer-motion";

import { useResumeStore } from "@/client/stores/resume";

// Extend the type to include a flag for social media fields
type ExtendedCustomField = ICustomField & {
  isSocial?: boolean;
};

type CustomFieldProps = {
  field: ExtendedCustomField;
  onChange: (field: ExtendedCustomField) => void;
  onRemove: (id: string) => void;
};

export const CustomField = ({ field, onChange, onRemove }: CustomFieldProps) => {
  const controls = useDragControls();

  const handleChange = (key: "icon" | "name" | "value", value: string) => {
    onChange({ ...field, [key]: value });
  };

  return (
    <Reorder.Item
      value={field}
      dragListener={false}
      dragControls={controls}
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50 }}
    >
      <div className="flex flex-col gap-2 rounded-lg border bg-muted/20 p-2 sm:flex-row sm:items-end sm:justify-between sm:border-0 sm:bg-transparent sm:p-0">
        {/* Controls row - on mobile shows drag + icon + delete, on desktop inline */}
        <div className="flex items-center justify-between sm:contents">
          <div className="flex items-center gap-1">
            <Button
              size="icon"
              variant="ghost"
              className="size-8 shrink-0 sm:size-10"
              onPointerDown={(event) => {
                controls.start(event);
              }}
            >
              <DotsSixVertical size={16} className="sm:size-5" />
            </Button>

            <Popover>
              <Tooltip content={field.isSocial ? t`Icon (Read-only)` : t`Icon`}>
                <PopoverTrigger asChild>
                  <Button size="icon" variant="ghost" className="size-8 shrink-0 sm:size-10" disabled={field.isSocial}>
                    {field.icon ? <i className={cn(`ph ph-${field.icon}`)} /> : <Envelope size={16} className="sm:size-5" />}
                  </Button>
                </PopoverTrigger>
              </Tooltip>
              {!field.isSocial && (
                <PopoverContent side="bottom" align="start" className="flex flex-col gap-y-1.5 p-1.5">
                  <Input
                    value={field.icon}
                    placeholder={t`Enter Phosphor Icon`}
                    onChange={(event) => {
                      onChange({ ...field, icon: event.target.value });
                    }}
                  />

                  <p className="text-xs opacity-80">
                    <Trans>
                      Visit{" "}
                      <a
                        href="https://phosphoricons.com/"
                        target="_blank"
                        className="underline"
                        rel="noopener noreferrer nofollow"
                      >
                        <Trans>
                          <Trans>Phosphor Icons</Trans>
                        </Trans>
                      </a>{" "}
                      for a list of available icons
                    </Trans>
                  </p>
                </PopoverContent>
              )}
            </Popover>
          </div>

          {/* Delete button - visible on mobile in header row */}
          <Button
            size="icon"
            variant="ghost"
            className="size-8 shrink-0 text-muted-foreground hover:text-destructive sm:hidden"
            onClick={() => {
              onRemove(field.id);
            }}
          >
            <X size={16} />
          </Button>
        </div>

        {/* Input fields - stack on mobile */}
        <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-center">
          <Input
            className="flex-1"
            placeholder={t`Name`}
            value={field.name}
            readOnly={field.isSocial}
            onChange={(event) => {
              handleChange("name", event.target.value);
            }}
          />

          <Input
            className="flex-1"
            placeholder={t`Value`}
            value={field.value}
            onChange={(event) => {
              handleChange("value", event.target.value);
            }}
          />
        </div>

        {/* Delete button - hidden on mobile, visible on desktop */}
        <Button
          size="icon"
          variant="ghost"
          className="hidden size-10 shrink-0 sm:flex"
          onClick={() => {
            onRemove(field.id);
          }}
        >
          <X />
        </Button>
      </div>
    </Reorder.Item>
  );
};

type Props = {
  className?: string;
};

export const CustomFieldsSection = ({ className }: Props) => {
  const setValue = useResumeStore((state) => state.setValue);
  const customFields = useResumeStore((state) => {
    try {
      return state.resume.data.basics.customFields;
    } catch {
      return [];
    }
  });

  const onChangeCustomField = (field: ExtendedCustomField) => {
    const index = customFields.findIndex((item) => item.id === field.id);
    const newCustomFields = JSON.parse(JSON.stringify(customFields));
    newCustomFields[index] = field;

    setValue("basics.customFields", newCustomFields);
  };

  const onReorderCustomFields = (values: ExtendedCustomField[]) => {
    setValue("basics.customFields", values);
  };

  const onRemoveCustomField = (id: string) => {
    setValue(
      "basics.customFields",
      customFields.filter((field) => field.id !== id),
    );
  };

  // Add a regular custom field button
  const handleAddCustomField = () => {
    setValue("basics.customFields", [
      ...customFields,
      { id: createId(), icon: "envelope", name: "", value: "" },
    ]);
  };

  return (
    <div className={cn("space-y-4", className)}>
      <AnimatePresence>
        <Reorder.Group
          axis="y"
          className="space-y-4"
          values={customFields}
          onReorder={onReorderCustomFields}
        >
          {customFields.map((field) => (
            <CustomField
              key={field.id}
              field={field}
              onChange={onChangeCustomField}
              onRemove={onRemoveCustomField}
            />
          ))}
        </Reorder.Group>
      </AnimatePresence>

      {/* <Button variant="link" onClick={handleAddCustomField}>
        <Plus className="me-2" />
        <span>{t`Add a custom field`}</span>
      </Button> */}
    </div>
  );
};
